/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState, useContext } from "react";
import getYMM, { getYMMAll } from "pms/product-finder";
import Image from "components/image";
import Loading from "components/loading";
import navigate from "@helpers/link-navigate";
import { ProductFinderContext } from "context/product-finder";
import useEffectWithoutFirstRender from "hooks/useEffectWithoutFirstRender";
import { useLocation } from "@reach/router"; 
import NProgress from "nprogress";
import sendLog from "@root/utils/new-relic-logger";
import NEW_RELIC_LEVELS from "@root/constants/NEW_RELIC_LEVELS";
import { DesktopDropdowns, MobileDropdowns, defaultBackGroundImages } from "./data";

// Starting state for every product finder (cars, boats, motorcycles, etc)
const initialState = {
    new: {
        make: {
            list: [], selected: null, name: "make",
        },
        model: {
            list: [], selected: null, name: "model",
        },
        year: {
            list: [], selected: null, name: "year",
        },
        option: {
            list: [], selected: null, name: "option",
        },
    },
    old: {
        make: {
            list: [], selected: null, name: "make",
        },
        year: {
            list: [], selected: null, name: "year",
        },
        model: {
            list: [], selected: null, name: "model",
        },
        option: {
            list: [], selected: null, name: "option",
        },
    },
};

const BackgroundImage = ({ bgImage, pathname, backgroundColor, isShoppingFeed }) => {
    const { asset } = bgImage || {};
    if (bgImage) return <Image className="element-as-bg" width={1240} image={asset} />;

    const defaultBackGroundImageExists = defaultBackGroundImages.find((backgroundImage) => pathname.includes(backgroundImage.key));

    if (defaultBackGroundImageExists && !isShoppingFeed) return <img className="element-as-bg" src={defaultBackGroundImageExists.image} alt={`${defaultBackGroundImageExists.key} - Covers`} />;

    return <div className="element-as-bg" style={{ background: backgroundColor || "#0E407D" }} />;
};

const Container = ({ bgImage, pathname, children, backgroundColor, isShoppingFeed }) => (
    <div className={`${isShoppingFeed ? "shopping-feed-desktop-container max-w-none" : "container max-w-3xl"}`}>
        <div className="relative">
            <BackgroundImage bgImage={bgImage} pathname={pathname} backgroundColor={backgroundColor} isShoppingFeed={isShoppingFeed} />
            <div className="shopping-feed-desktop-container-items flex justify-center md:py-16 py-11 md:px-8 px-10 flex-col relative">
                {children}
            </div>
        </div>
    </div>
);

const Error = ({ bgImage, pathname, backgroundColor }) => (
    <Container bgImage={bgImage} pathname={pathname} backgroundColor={backgroundColor}>
        <h1 className="text-white text-center mb-10 uppercase font-semibold"> 
            Request could not be processed, please try again ...
        </h1>
    </Container>
);

const ProductFinder = ({ newFinder, data: { title, subtitle, initialMake, initialData, collectionType, bgImage, backgroundColor, isShoppingFeed } }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [manualChange, setManualChange] = useState(false);
    const { productFinder, setProductFinder } = useContext(ProductFinderContext);
    const location = useLocation();
    const instance = newFinder ? "new" : "old";

    // If Initial Make already exists, the first dropdown is hidden, so the starting dropdown will be the second one in the row
    const firstDropdownIndex = initialMake ? 1 : 0;

    const handleGoToCollection = (selectedModelOption) => {
        // If there are no options, it means we have not yet reached the last dropdown, return early
        if (productFinder[collectionType]?.option?.list?.length === 0) return;

        NProgress.start();

        setLoading(true);
        const selectedCollection = productFinder[collectionType]?.option?.list?.find((collection) => collection?.option === selectedModelOption) || productFinder[collectionType]?.option?.list[0];
        const fullUrl = `/collections/${selectedCollection.handle}/${selectedCollection.year}`;
        navigate(fullUrl);
    };

    const updateNextDropdownList = async (url, dropdownIndex) => {
        setLoading(true);
        try {
            // nextVehiclePropertyList means that, if we have selected a Make in the Make dropdown, this will return a list of Years for that Make (and so forth for other selections)
            const { data: nextVehiclePropertyList } = await getYMM(`${collectionType}${url}`, newFinder);

            const productFinderDropdown = Object.values(productFinder[collectionType] || initialState[instance])[dropdownIndex + 1]?.name;
            
            // If we go back to select a new Make, reset Model, populate Year List again
            if (dropdownIndex === 0) {
                setProductFinder((prevState) => ({
                    ...prevState,
                    [collectionType]: {
                        ...initialState[instance],
                        make: prevState[collectionType].make,
                        [productFinderDropdown]: {
                            ...prevState[collectionType][productFinderDropdown], selected: null, list: nextVehiclePropertyList,
                        },
                    },
                }));
                return setLoading(false);
            }

            setProductFinder((prevState) => ({
                ...prevState,
                [collectionType]: {
                    ...prevState[collectionType],
                    [productFinderDropdown]: {
                        ...prevState[collectionType][productFinderDropdown], selected: null, list: nextVehiclePropertyList,
                    },
                },
            }));

            return setLoading(false);
        } catch (err) {
            sendLog(`[Product Finder] Error: ${err}, URL: ${collectionType}${url}`, NEW_RELIC_LEVELS.FATAL);
            setError(true);
        }

        return setLoading(false);
    };

    const handleSelect = async (dropdownName, selectedValue) => {

        setProductFinder((prevState) => ({
            ...prevState,
            [collectionType]: ({
                ...prevState[collectionType],
                [dropdownName]: { ...prevState[collectionType][dropdownName], selected: selectedValue }, 
            }),
        }));
        
        // Which dropdown was just selected
        const dropdownIndex = Object.values(productFinder[collectionType]).findIndex((dropdown) => dropdown.name === dropdownName);

        if (dropdownIndex !== Object.values(productFinder[collectionType]).length - 1) {
            // Since a full URL might be something like this: /vehicle_details?type=car&make=BMW&year=2022,
            // this gets the make/year (if they exist)
            const previousURLs = Object.values(productFinder[collectionType])
                .slice(0, dropdownIndex)
                .map((dropdown) => dropdown.selected !== null && `&${dropdown.name}=${dropdown.selected}`)
                .filter((url) => url !== false)
                .join("");

            // Creates a new URL to be added (if make => &make=x, if year => &year=x)
            const selectedPropertyURL = `&${dropdownName}=${selectedValue}`;
            const fullURL = previousURLs + selectedPropertyURL;
            await updateNextDropdownList(fullURL, dropdownIndex);

            setManualChange(true);
        } else {
            handleGoToCollection(selectedValue);
        }
    };
    
    // Function called on Modal Dropdowns, resets state for the current dropdown, on click we go back from the current dropdown to the previous one
    const handleGoBack = (dropdownName) => {
        const dropdownIndex = Object.values(productFinder[collectionType] || {})
            .findIndex((dropdownEl) => dropdownEl.name === dropdownName);

        const dropdowns = Object.values(productFinder[collectionType] || initialState[instance]);
        const dropdownsToReset = dropdowns.slice(dropdownIndex, dropdowns.length);

        const updatedProductFinder = dropdownsToReset.reduce((acc, dropdown) => {
            acc[dropdown.name] = {
                ...dropdown, 
                list: dropdowns[firstDropdownIndex].name === dropdown.name ? dropdown.list : [],
                selected: null, 
            };
            return acc; 
        }, {});

        setProductFinder((prevState) => ({
            ...prevState,
            [collectionType]: {
                ...prevState[collectionType],
                ...updatedProductFinder,
            },
        }));
    };

    // Called only on the first render
    const handleInitialState = async () => {
        try {
            setLoading(true);

            if( initialData ) {

                let path = `${collectionType}`;

                if( initialData.make ) { path += `&make=${initialData.make}`; }
                if( initialData.year ) { path += `&year=${initialData.year}`; }
                if( initialData.model ) { path += `&model=${initialData.model}`; }
                if( initialData.option ) { path += `&option=${initialData.option}`; }

                const data = await getYMMAll(path, newFinder);


                let dataObj = {
                    ...initialState[instance],
                }

                if(data.makes) {
                    dataObj.make = { ...initialState[instance].make, selected: initialData.make ?? null, list: data.makes }
                }

                if(data.years) {
                    dataObj.year = { ...initialState[instance].year, selected: initialData.year ?? null, list: data.years }
                }

                if(data.models) {
                    dataObj.model = { ...initialState[instance].model, selected: initialData.model ?? null, list: data.models }
                }

                if(data.options) {
                    dataObj.option = { ...initialState[instance].option, selected: initialData.option ?? null, list: data.options }
                }

                setProductFinder((prevState) => ({
                    ...prevState,
                    [collectionType]: dataObj,
                }));

                setLoading(false);

            } else {
                const {data: makeList, meta} = await getYMM(collectionType, newFinder);

                const selectedMake = initialMake
                    ? {selected: initialMake, list: makeList}
                    : {list: makeList};

                setProductFinder((prevState) => ({
                    ...prevState,
                    [collectionType]: ({
                        ...initialState[instance],
                        make: {...initialState[instance].make, ...selectedMake},
                        option: {...initialState[instance].option, hidden: !meta?.has_options},
                    }),
                    hiddenOptions: {
                        ...prevState.hiddenOptions,
                        [collectionType]: !meta?.has_options,
                    },
                }));

                if (initialMake) {
                    const yearListURL = `&${initialState[instance].make.name}=${initialMake}`;
                    updateNextDropdownList(yearListURL, 0);
                } else {
                    setLoading(false);
                }
            }
        } catch (err) {
            sendLog(`[Product Finder] Error: ${err}, URL: ${collectionType}`, NEW_RELIC_LEVELS.FATAL);
            setError(true);
        }
    }; 

    // Called when the component is first mounted, since we initially need a List of Makes and optionally a selected Make coming from Sanity
    const setStateOnInitialMount = async () => {
        const { make } = productFinder[collectionType] || {};

        // If our make.selected is already the same as the initialMake, return early
        // This handles the case where we navigate away and return to the first landing page
        if (initialMake && make?.list && make?.selected === initialMake) return;

        // If there is no initialMake, but there is a list, there is no need to re-update the state
        if (!initialMake && make?.list) return;

        await handleInitialState();
    };

    useEffect(() => {
        setStateOnInitialMount();
    }, []);

    /* 
      Should navigate to the selected collection page if there is only one option in the option.list.
      Using useEffectWithoutFirstRender, since if there is only one option, which navigates us to that Option Page
      if we navigate back, it shouldn't navigate us again to the Option Page (in the process, creating an infinite loop)
    */
    useEffectWithoutFirstRender(() => {
        if (productFinder[collectionType]?.option?.list?.length === 1 && manualChange) {
            const collectionModel = productFinder[collectionType]?.option?.list[0];
            const fullUrl = `/collections/${collectionModel?.handle}/${collectionModel?.year}`;

            setProductFinder((prevState) => ({
                ...prevState,
                [collectionType]: {
                    ...prevState[collectionType],
                    option: { ...prevState[collectionType].option, selected: collectionModel.option },
                },
            }));

            NProgress.start();

            navigate(fullUrl);
        } else NProgress.done();
    }, [productFinder[collectionType]?.option?.list, manualChange]);

    if (!collectionType || error) return <Error bgImage={bgImage} pathname={location.pathname} backgroundColor={backgroundColor} />;

    return (
        <Container bgImage={bgImage} pathname={location.pathname} backgroundColor={backgroundColor} isShoppingFeed={isShoppingFeed}>
            <h1 className="text-white text-center mb-10 uppercase font-semibold"> 
                {title}
            </h1>
            <h2 className="text-white text-center mb-5 uppercase font-semibold"> 
                {subtitle}
            </h2>
            {loading && <Loading className={`fixed lg:absolute ${isShoppingFeed ? 'rounded-[21px]' : ''}`} />}

            <DesktopDropdowns
                dropdownsArr={Object.values(productFinder[collectionType] || initialState[instance])}
                initialMake={initialMake}
                onSelect={handleSelect}
                hideOptionDropdown={productFinder?.hiddenOptions ? productFinder.hiddenOptions[collectionType] : false}
                onGoToCollection={handleGoToCollection}
                className="lg:flex hidden ymm-dropdown-container"
                isShoppingFeed={isShoppingFeed}
            />
            <MobileDropdowns
                dropdownsArr={Object.values(productFinder[collectionType] || initialState[instance])}
                initialMake={initialMake}
                onSelect={handleSelect}
                hideOptionDropdown={productFinder?.hiddenOptions ? productFinder.hiddenOptions[collectionType] : false}
                onGoToCollection={handleGoToCollection}
                className="lg:hidden flex"
                onGoBack={handleGoBack}
                firstDropdownIndex={firstDropdownIndex}
                isShoppingFeed={isShoppingFeed}
            />
        </Container>
    );
};

export default ProductFinder;
